// A commissioner taking back a move (COMMISSIONER_OVERRIDES.md "Reversing a
// move", J2). What goes where, the refund, and what does NOT come back — the
// waiver order and points already scored — said before it is done; a reason;
// and the server's sentence when it refuses.

import { useState } from 'react';
import type { PlayerSummary } from '@fsp/domain';
import { Sheet } from '../../kit/overlays';
import { tell, Button, Callout, Stack, Text, TextArea } from '../../kit/ui';
import { messageOf } from '../../data/api';
import { reverseMove } from '../../data/moves';

/** The move as the sheet describes it, from a log line or a claim's result. */
export interface ReverseTarget {
  transactionId: string;
  kind: 'add' | 'drop' | 'claim' | 'trade';
  teamName: string;
  /** Players the move brought to the team. */
  added: readonly PlayerSummary[];
  /** Players the move sent away from it. */
  dropped: readonly PlayerSummary[];
  /** A claim's winning bid, null when it had none. */
  bid: number | null;
}

function names(players: readonly PlayerSummary[]): string {
  const n = players.map((p) => p.name);
  if (n.length <= 1) return n.join('');
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

/** What the reversal will do, in the order it happens. */
export function reverseConsequences(t: ReverseTarget): string[] {
  const out: string[] = [];
  if (t.kind === 'trade') {
    out.push(
      'Both sides of the trade are reversed: each player goes back to the team that traded him.',
    );
  } else {
    if (t.added.length > 0)
      out.push(
        t.added.length === 1
          ? `${names(t.added)} leaves ${t.teamName} and goes on waivers if the league has them, so nobody gets a jump on him.`
          : `${names(t.added)} leave ${t.teamName} and go on waivers if the league has them, so nobody gets a jump on them.`,
      );
    if (t.dropped.length > 0)
      out.push(
        `${names(t.dropped)} ${t.dropped.length === 1 ? 'comes' : 'come'} back to ${t.teamName}.`,
      );
    if (t.bid !== null && t.bid > 0) out.push(`The $${t.bid} bid is refunded.`);
  }
  out.push(
    'The waiver order stays as it is, and points already scored stay where they were scored.',
  );
  out.push(
    'Every manager it touches is told, and the league chat and the commissioner log say so, with your reason.',
  );
  return out;
}

const TITLE: Record<ReverseTarget['kind'], string> = {
  add: 'Reverse this add',
  drop: 'Reverse this drop',
  claim: 'Reverse this claim',
  trade: 'Reverse this trade',
};

export function ReverseMoveSheet({
  target,
  leagueId,
  onClose,
}: {
  target: ReverseTarget | null;
  leagueId: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const close = () => {
    setReason('');
    setProblem(null);
    onClose();
  };

  const save = async () => {
    if (target === null) return;
    setSaving(true);
    setProblem(null);
    const reply = await reverseMove(leagueId, target.transactionId, reason.trim());
    setSaving(false);
    if (reply.status !== 'ok')
      return setProblem(
        messageOf(
          reply,
          'That did not go through.',
          'Could not reach the site, so we cannot tell whether that was reversed. Try again: if it was, you will be told so.',
        ),
      );
    close();
    tell('Reversed.');
  };

  return (
    <Sheet opened={target !== null} onClose={close} title={target ? TITLE[target.kind] : undefined}>
      {target ? (
        <Stack gap={3}>
          <Callout>
            <Stack gap={1}>
              {reverseConsequences(target).map((line) => (
                <Text key={line} block>
                  {line}
                </Text>
              ))}
            </Stack>
          </Callout>
          <TextArea
            label="Why"
            placeholder="The reason, in a sentence the league will read"
            value={reason}
            onChange={setReason}
            maxLength={300}
          />
          {problem !== null ? <Callout tone="bad">{problem}</Callout> : null}
          <Button
            onClick={() => void save()}
            disabled={reason.trim() === '' || saving}
            loading={saving}
          >
            Reverse
          </Button>
        </Stack>
      ) : null}
    </Sheet>
  );
}
